import { useState } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Loader2 } from "lucide-react"
import * as programApi from "../../api/outreach-program.api"
import { formatCentral, restartCandidates, restartedList, apiErrorMessage } from "../../lib/program"
import type { RestartRequest, RestartResult } from "../../types/outreach-program.types"

/**
 * D3: launches never re-enroll CONSENT_MAX_RETRIES_REACHED patients. This
 * starts a new attempt cycle for a cohort: dry-run preview first, then apply.
 */
export function RestartMaxRetries({ orgId, onApplied }: { orgId: string; onApplied: () => void }) {
  const [days, setDays] = useState("60")
  const [limit, setLimit] = useState("")
  const [preview, setPreview] = useState<{ req: RestartRequest; res: RestartResult } | null>(null)
  const [applied, setApplied] = useState<RestartResult | null>(null)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const nDays = Number(days)
  const nLimit = limit.trim() ? Number(limit) : undefined
  const valid = Number.isInteger(nDays) && nDays >= 0 && (nLimit === undefined || (Number.isInteger(nLimit) && nLimit > 0))

  const cands = restartCandidates(preview?.res)
  const previewIds = cands.list.map((c) => c.patient_id)

  const run = async (dryRun: boolean) => {
    const req: RestartRequest = dryRun
      ? { last_call_older_than_days: nDays, ...(nLimit ? { limit: nLimit } : {}), dry_run: true }
      : {
          ...preview!.req,
          // Apply exactly the previewed patients when the preview listed them.
          ...(previewIds.length ? { patient_ids: previewIds } : {}),
          dry_run: false,
        }
    setBusy(true)
    setError(null)
    try {
      const res = await programApi.restartMaxRetries(orgId, req)
      if (dryRun) {
        setPreview({ req, res })
        setApplied(null)
      } else {
        setApplied(res)
        setPreview(null)
        setConfirmOpen(false)
        onApplied()
      }
    } catch (err: any) {
      setError(apiErrorMessage(err, "Request failed"))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-end gap-3">
        <div className="space-y-1">
          <Label htmlFor="restart-days" className="text-xs">Last call older than (days)</Label>
          <Input id="restart-days" type="number" min={0} className="w-32" value={days} onChange={(e) => setDays(e.target.value)} />
        </div>
        <div className="space-y-1">
          <Label htmlFor="restart-limit" className="text-xs">Limit (optional)</Label>
          <Input id="restart-limit" type="number" min={1} className="w-32" value={limit} onChange={(e) => setLimit(e.target.value)} />
        </div>
        <Button size="sm" variant="outline" disabled={!valid || busy} onClick={() => run(true)}>
          {busy && !confirmOpen && <Loader2 className="animate-spin" />}
          Preview (dry run)
        </Button>
      </div>
      {error && <p className="text-sm text-destructive">{error}</p>}

      {preview && (
        <div className="rounded-lg border p-3 space-y-2 text-sm" data-testid="restart-preview">
          <p>
            <span className="font-semibold tabular-nums">{cands.count}</span> max-retries patient
            {cands.count === 1 ? "" : "s"} would restart (last call over {preview.req.last_call_older_than_days} days ago
            {preview.req.limit ? `, limit ${preview.req.limit}` : ""}).
            {preview.res.skipped?.length > 0 && ` ${preview.res.skipped.length} skipped.`}
          </p>
          {cands.list.length > 0 && (
            <ul className="max-h-48 overflow-auto text-xs text-muted-foreground">
              {cands.list.map((c) => (
                <li key={c.patient_id}>
                  {c.patient_name || c.patient_id}
                  {c.last_call_at ? ` · last call ${formatCentral(c.last_call_at as string)}` : ""}
                </li>
              ))}
            </ul>
          )}
          {preview.res.skipped?.length > 0 && (
            <ul className="text-xs text-muted-foreground">
              {preview.res.skipped.slice(0, 10).map((s) => (
                <li key={s.patient_id}>
                  skipped {s.patient_id}: {s.reason}
                </li>
              ))}
            </ul>
          )}
          <Button size="sm" disabled={cands.count === 0} onClick={() => setConfirmOpen(true)}>
            Restart {cands.count} patient{cands.count === 1 ? "" : "s"}
          </Button>
        </div>
      )}

      {applied && (
        <p className="text-sm" data-testid="restart-applied">
          Restarted {restartedList(applied).length} patient{restartedList(applied).length === 1 ? "" : "s"}
          {applied.skipped?.length ? `; ${applied.skipped.length} skipped (${applied.skipped.map((s) => s.reason).filter((r, i, a) => a.indexOf(r) === i).join(", ")})` : ""}.
        </p>
      )}

      <Dialog open={confirmOpen} onOpenChange={(o) => !busy && setConfirmOpen(o)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Restart {cands.count} max-retries patients?</DialogTitle>
            <DialogDescription>
              Each moves back to CONSENT_NEEDED with a new attempt cycle (attempts count per enrollment). The program
              then calls them under its normal cadence and caps. Only the previewed patients are sent; any that no longer
              qualify are skipped and reported.
            </DialogDescription>
          </DialogHeader>
          {error && <p className="text-sm text-destructive">{error}</p>}
          <DialogFooter>
            <Button variant="outline" disabled={busy} onClick={() => setConfirmOpen(false)}>
              Cancel
            </Button>
            <Button disabled={busy} onClick={() => run(false)}>
              {busy && <Loader2 className="animate-spin" />}
              Restart
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
